import { Organization } from "@/types/organization"
import { RegisterGateTypes } from "@/types/register-gate"
import { CurrentUser, LoginAuthProvider, UserPermission } from "@/types/user"
import { DeepCompact } from "@/types/util"

import { TurnstileModes } from "./turnstile"

export interface EmailCredentials {
  email: string
  password: string
  cf_token: string
  type: "sign-in" | "register"
  verify_code?: string
  verify_web_handoff_code?: string
  organization_email_opt_in?: boolean
  numeric_email_validation_code?: boolean
}

export type CredentialsInputs =
  | EmailCredentials
  | {
      type: "apple"
      code: string
      id_token: string
      email?: string
      first_name?: string
      last_name?: string
    }
  | {
      type: "google"
      credential: string
    }

export interface ProviderAuthInputs {
  provider: string
  provider_account_id: string
  email?: string
  image_url?: string
  first_name?: string
  last_name?: string
  cf_mode?: TurnstileModes
}

export interface SignInComponentProps {
  callbackUrl: string | null | undefined
  setAuthComponent: (
    authComponent: AuthComponent,
    email?: string,
    providers?: LoginAuthProvider[]
  ) => void
  onSubmit?: () => void
  email?: string
}

export interface AuthenticationResponse {
  apiServerToken?: string
  uuid?: string
  id?: string
  error?: string
}

export type AuthComponent =
  | "auth-form"
  | "forgotten"
  | "verify_email"
  | "reset_password"

export type AuthFlowStep = "email" | "signin" | "register"

export type ForgotPasswordStep = "form" | "success" | "mismatch"

export interface ForgotPasswordState {
  step: ForgotPasswordStep
  submitting: boolean
}

export type EmailVerifyStep = "form" | "verified"

export interface EmailVerifyState {
  step: EmailVerifyStep
  verifying: boolean
  resending: boolean
  message?: string
  error?: string
  displayEmail?: string
}

export interface ResetPasswordProps {
  error?: string
  resetPasswordToken: string
  callbackUrl?: string
}

export interface ResetPasswordInputs {
  password: string
  confirmPassword?: string
}

export interface ResetPasswordRequestBody {
  reset_password_token: string
  new_password: string
}

export interface ServerErrorBody {
  error: string
}

export interface AuthCookie {
  apiServerToken: string
}

export interface AuthResponse {
  token: string
  user: CurrentUser
}

export type SharedAuthParams = {
  cf_token?: string
}

export type SignInApiParams = {
  email: string
  password: string
} & SharedAuthParams

export type RegisterApiParams = {
  email: string
  password: string
  first_name?: string
  last_name?: string
} & SharedAuthParams

export type GoogleApiParams = {
  code: string
} & SharedAuthParams

export type AppleApiParams = {
  code: string
  id_token: string
  email?: string
  first_name?: string
  last_name?: string
} & SharedAuthParams

export type EmbeddedSignInApiParams = {
  email: string
  password: string
  organization_email_opt_in?: boolean
  cf_token?: string
}

export type EmbeddedRegisterApiParams = {
  email: string
  password: string
  first_name?: string
  last_name?: string
  organization_email_opt_in?: boolean
  cf_token?: string
}

export type SignInParams = {
  email: string
  password: string
  callbackUrl?: string
  cf_token?: string
  verify_code?: string
}

export type RegisterParams = {
  email: string
  password: string
  first_name?: string
  last_name?: string
  callbackUrl?: string
  cf_token?: string
  organization_email_opt_in?: boolean
}

// Minimal params for Google sign-in - thunk fetches context internally
export type GoogleSignInParams = {
  code: string
}

export type AppleSignInParams = {
  code: string
  id_token: string
  email?: string
  first_name?: string
  last_name?: string
}

export type WebHandoffParams = {
  code: string
  callbackUrl: string
}

export interface CreateAnonParams {
  cf_token?: string
}

export interface EmbeddedCreateAnonParams extends CreateAnonParams {
  orgid: string
  hostname?: string
  sso_code?: string
  nocookie?: string
}

export interface CreateAnonymousUserApiParams {
  tester?: string
  orgid?: string
  cf_token?: string
  type?: string
}

export interface AuthToken {
  value: string
  expires_in: number
}

export interface RefreshToken {
  value: string
  expires_in: number | null
}

export interface AuthV2Response {
  registration_occurred?: boolean
  token: string
  auth_token: AuthToken
  refresh_token?: RefreshToken
  permissions: UserPermission[]
  user: CurrentUser
  orgid?: string
  organization?: Organization
}

export interface RefreshTokenParams {
  refresh_token: string
}

export interface VerifyEmailParams {
  verify_code: string
}

export interface SignOutParams {
  refresh_token?: string
}

export interface EmbeddedAuthResponse {
  token: string
  auth_token: AuthToken
  refresh_token?: RefreshToken
  user: CurrentUser
  permissions: UserPermission[]
  orgid?: string
  organization?: Organization
}

export interface LegacyJWTPayload {
  exp: number
  user_id: number
  user?: {
    user_ref: string
    email?: string
    first_name?: string
    last_name?: string
    [key: string]: unknown
  }
  permissions?: UserPermission[]
  session_id?: string
  orgid?: string
  organization?: Organization
  app?: string

  [key: string]: unknown
}

export interface JWTPayload {
  user_id: number
  user: CurrentUser
  permissions: UserPermission[]
  session_id: string
  orgid?: string
  organization?: Organization
  app: "web" | "ios" | "embedded"
  exp: number
  iat: number
}

export type LegacySession = DeepCompact<{
  is_legacy: true
  user: CurrentUser
  permissions: UserPermission[]
  expires_in: number
  session_id?: string
  organization?: Organization
}>

export type SessionV2 = DeepCompact<{
  is_legacy: false
  user_id: number
  user: CurrentUser
  permissions: UserPermission[]
  session_id: string
  orgid?: string
  organization?: Organization
  app: "web" | "ios" | "embedded"
  expires_in: number
}>

export type Session = LegacySession | SessionV2

export const AUTH_TOKEN_COOKIE = "mindtrip.auth_token"
export const REFRESH_TOKEN_COOKIE = "mindtrip.refresh_token"

// Legacy auth cookies
// [AuthCleanup] Migration: Supports legacy anonymous users - monitor usage before removal
// This cookie stores the old format: {"apiServerToken": "jwt_token_here"}
export const OLD_AUTH_COOKIE = "mindtrip.auth"

// NextAuth cookie names — cleared on sign-in/sign-out for browsers that still have them

// NextAuth cookies - Production
export const NEXTAUTH_SESSION_TOKEN_PROD =
  "__Secure-next-auth.session-token.mindtrip1"
export const PKCE_CODE_VERIFIER_PROD = "__Secure-next-auth.pkce.code_verifier"
export const NEXTAUTH_CSRF_TOKEN_PROD = "__Secure-next-auth.csrf-token"
export const NEXTAUTH_CALLBACK_URL_PROD = "__Secure-next-auth.callback-url"

// NextAuth cookies - Development
export const NEXTAUTH_SESSION_TOKEN_DEV = "next-auth.session-token"
export const PKCE_CODE_VERIFIER_DEV = "next-auth.pkce.code_verifier"
export const NEXTAUTH_CSRF_TOKEN_DEV = "next-auth.csrf-token"
export const NEXTAUTH_CALLBACK_URL_DEV = "next-auth.callback-url"

// Session cookies
export const SESSION_PROPS_COOKIE = "mindtrip.session_props"

// Carries the client-resolved locale tag (e.g. "fr-FR") so the server can read it.
export const MINDTRIP_LOCALE_COOKIE = "mindtrip.locale"

export const BASIC_AUTH_COOKIE = "mindtrip.basic-auth"
export const BASIC_AUTH_HEADER = "mindtrip-basic-auth"
export const BASIC_AUTH_QUERY_PARAM = "mt:basic-auth"
export const EMBEDDED_BASIC_AUTH_LOCAL_STORAGE_KEY =
  "embedded.mindtrip.basic-auth"

export const SERVER_AUTH_TOKEN_OVERRIDE_HEADER = "mt-server-auth-token-override"

export type RegisterGateDialogInfo = {
  type: RegisterGateTypes
  email?: string
  emailLocked?: boolean
  queryParams?: string
  setupTravelAssistantAfterSignup?: boolean
}
